import { Box, Modal } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { useEffect, useMemo, useRef, useState } from "react";
import logoAnimation from "../assets/white.json";
import { useGlobalContext } from "./GlobalContext/GlobalContext";
import { Header } from "./Header/header";
import { ProjectData, Projects } from "./NavigationData/ProjectData";
import { AnimatedLayout } from "./Router/AnimatedLayout";
import { RoutingLink } from "./Router/RoutingLink";

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function ProjectCover({ project }: { project: ProjectData }) {
  return (
    <motion.img
      whileHover={{
        scale: 0.95,
      }}
      src={`/images/${project.name}/cover.jpg`}
      style={{
        display: "block",
        flex: "1 1 0",
        objectFit: "contain",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        aspectRatio: 1,
        height: "100%",
      }}
    />
  );
}

function HomePage() {
  const split_projects = chunkArray(Projects, 3);

  return (
    <AnimatedLayout>
      <Header />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          placeItems: "center",
          padding: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "auto",
            maxHeight: "100%",
            alignSelf: "center",
            justifySelf: "center",
            flex: "0 0 auto",
            minWidth: 0,
            minHeight: 0,
            aspectRatio: "1",
          }}
        >
          <Box sx={{ display: "flex", flex: "1 1 0", flexDirection: "column", minHeight: 0, gap: "8px" }}>
            {split_projects.map((section, index) => (
              <Box key={`section-#${index}`} sx={{ display: "flex", flex: "1 1 0", minHeight: 0, gap: "8px" }}>
                {section.map((project) => (
                  <Box
                    key={project.name}
                    component={RoutingLink}
                    to={`projects/${project.name}`}
                    sx={{ display: "contents" }}
                  >
                    <ProjectCover project={project} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </AnimatedLayout>
  );
}

function StartupAnimation() {
  const [isMovingUp, setIsMovingUp] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [logoIsMoving, setLogoIsMoving] = useState(true);
  const [speedUp, setSpeedUp] = useState(false);
  const lottie = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (speedUp) {
      lottie?.current?.setSpeed(5);
    }
  }, [lottie, speedUp]);

  useEffect(() => {
    if (!isMovingUp) {
      localStorage.setItem("alreadyLoaded", "true");
    }
  }, [isMovingUp]);

  const exitDuration = speedUp ? 1 / 5 : 1;

  return (
    <Modal open={isMovingUp} hideBackdrop disableEnforceFocus sx={{ width: "100vw", height: "100vh" }}>
      <Box>
        <AnimatePresence>
          {logoIsMoving && (
            <motion.div
              exit={{ height: "0vh" }}
              transition={{ duration: exitDuration }}
              onAnimationComplete={() => setIsMovingUp(false)}
              style={{ width: "100vw", height: "100vh", backgroundColor: "black" }}
            >
              <AnimatePresence>
                {isLoading && (
                  <motion.div
                    exit={{ scale: 0 }}
                    transition={{ duration: exitDuration }}
                    onAnimationComplete={() => setLogoIsMoving(false)}
                    onMouseDown={() => setSpeedUp(true)}
                    onTouchStart={() => setSpeedUp(true)}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <Lottie
                      animationData={logoAnimation}
                      loop={false}
                      lottieRef={lottie}
                      onComplete={() => setIsLoading(false)}
                      style={{
                        width: "100%",
                        height: "100%",
                        alignSelf: "center",
                        justifySelf: "center",
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Modal>
  );
}

export function Home() {
  const context = useGlobalContext();
  const firstPageLoaded = useMemo(() => context.firstPageLoading, []);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, backgroundColor: "#1414149f" }}
    >
      {firstPageLoaded && <StartupAnimation />}
      <HomePage />
    </Box>
  );
}
